import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Users, BarChart3, Briefcase, Award, ClipboardList, PenTool, TrendingUp, CheckCircle, Wallet } from 'lucide-react';
import {
  reportsApi,
  TeamPerformanceReportDto,
  ReportFilterDto,
  formatCurrency,
  formatPercentage,
  handleApiError
} from '../../services/reportsService';

const ReportContainer = styled.div`
  padding: 0;
`;

const SummarySection = styled.div`
  margin-bottom: 28px;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryItem = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  padding-top: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const SummaryItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SummaryItemIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div<{ $color?: string }>`
  color: ${props => props.$color || 'var(--primary-color)'};
  display: flex;
  align-items: center;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  table-layout: fixed;
`;

const Th = styled.th<{ $width?: string }>`
  background: var(--background-color);
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  width: ${props => props.$width || 'auto'};
`;

const Td = styled.td`
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percentage: number; $color?: string }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props => props.$color || 'var(--primary-color)'};
  transition: width 0.3s ease;
  border-radius: 3px;
  filter: saturate(0.7) brightness(0.9);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: var(--error-color);
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

const NameCell = styled.div`
  position: relative;
  display: inline-block;
`;

const TopPerformerBadge = styled.span`
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: #fef3c7;
  color: #d97706;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  filter: saturate(0.7) brightness(0.9);
  white-space: nowrap;
`;

interface TeamPerformanceReportProps {
  filter: ReportFilterDto;
}

const TeamPerformanceReport: React.FC<TeamPerformanceReportProps> = ({ filter }) => {
  const [report, setReport] = useState<TeamPerformanceReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsApi.getTeamPerformanceReport(filter);
      setReport(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingContainer>טוען דוח ביצועי צוות...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!report) {
    return null;
  }

  const summary = report.monthlySummary;

  return (
    <ReportContainer>
      {/* Monthly Summary */}
      <SummarySection>
        <SummaryHeader>
          <SummaryIcon>
            <BarChart3 size={20} />
          </SummaryIcon>
          <SummaryTitle>סיכום חודשי</SummaryTitle>
        </SummaryHeader>
        <SummaryGrid>
          <SummaryItem $accentColor="var(--primary-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--primary-color)">
                <ClipboardList size={20} />
              </SummaryItemIcon>
              <SummaryLabel>לידים חדשים</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalNewLeads}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--success-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--success-color)">
                <PenTool size={20} />
              </SummaryItemIcon>
              <SummaryLabel>חתימות</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalSignatures}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="#6366f1">
            <SummaryItemHeader>
              <SummaryItemIcon $color="#6366f1">
                <TrendingUp size={20} />
              </SummaryItemIcon>
              <SummaryLabel>אחוז המרה</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{formatPercentage(summary.conversionPercentage)}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--info-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--info-color)">
                <CheckCircle size={20} />
              </SummaryItemIcon>
              <SummaryLabel>תיקים מאושרים</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalApprovedCases}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--warning-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--warning-color)">
                <Wallet size={20} />
              </SummaryItemIcon>
              <SummaryLabel>סכום גמלאות</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue style={{ fontSize: '22px' }}>
              {formatCurrency(summary.totalBenefitAmount)}
            </SummaryValue>
          </SummaryItem>
        </SummaryGrid>
      </SummarySection>

      {/* Sales Reps Performance */}
      <Section>
        <SectionHeader>
          <SectionIcon $color="var(--primary-color)">
            <Users size={18} />
          </SectionIcon>
          <SectionTitle>ביצועי נציגי מכירות</SectionTitle>
        </SectionHeader>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th $width="20%">שם</Th>
                <Th $width="20%">לידים שהתקבלו</Th>
                <Th $width="20%">חתימות</Th>
                <Th $width="20%">אחוז הצלחה</Th>
                <Th $width="20%">התקדמות</Th>
              </tr>
            </thead>
            <tbody>
              {report.salesRepsPerformance.map((rep, index) => (
                <Tr key={rep.userId}>
                  <Td style={{ fontWeight: 600 }}>
                    <NameCell>
                      {index === 0 && <TopPerformerBadge><Award size={12} /> מוביל</TopPerformerBadge>}
                      <span>{rep.name}</span>
                    </NameCell>
                  </Td>
                  <Td>{rep.leadsReceivedThisMonth}</Td>
                  <Td style={{ fontWeight: 600, color: '#16a34a', filter: 'saturate(0.7) brightness(0.85)' }}>
                    {rep.signedThisMonth}
                  </Td>
                  <Td>{formatPercentage(rep.successPercentage)}</Td>
                  <Td>
                    <ProgressBar>
                      <ProgressFill
                        $percentage={rep.successPercentage}
                        $color={rep.successPercentage >= 50 ? 'var(--success-color)' : 'var(--warning-color)'}
                      />
                    </ProgressBar>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </Section>

      {/* Case Managers Performance */}
      <Section>
        <SectionHeader>
          <SectionIcon $color="var(--primary-color)">
            <Briefcase size={18} />
          </SectionIcon>
          <SectionTitle>ביצועי מנהלי תיקים</SectionTitle>
        </SectionHeader>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th $width="20%">שם</Th>
                <Th $width="20%">תיקים מנוהלים</Th>
                <Th $width="20%">תיקים מאושרים</Th>
                <Th $width="20%">סכום גמלה שהושג</Th>
                <Th $width="20%">הכנסה צפויה</Th>
              </tr>
            </thead>
            <tbody>
              {report.caseManagersPerformance.map((manager, index) => (
                <Tr key={manager.userId}>
                  <Td style={{ fontWeight: 600 }}>
                    <NameCell>
                      {index === 0 && <TopPerformerBadge><Award size={12} /> מוביל</TopPerformerBadge>}
                      <span>{manager.name}</span>
                    </NameCell>
                  </Td>
                  <Td>{manager.casesManaged}</Td>
                  <Td style={{ fontWeight: 600, color: '#16a34a', filter: 'saturate(0.7) brightness(0.85)' }}>
                    {manager.approvedCases}
                  </Td>
                  <Td>{formatCurrency(manager.totalBenefitAmount)}</Td>
                  <Td style={{ fontWeight: 600, color: '#2563eb', filter: 'saturate(0.7) brightness(0.85)' }}>
                    {formatCurrency(manager.expectedIncomeFromCases)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </Section>
    </ReportContainer>
  );
};

export default TeamPerformanceReport;
